import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { validatePasswordStrength } from "../../utils/crypto/vault-crypto";

interface MasterPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
  mode: "create" | "unlock";
  title?: string;
  label?: string;
  placeholder?: string;
  description?: string;
  onResetRequest?: () => void;
  onSwitchToCreate?: () => void;
}

const MasterPasswordModal: React.FC<MasterPasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  title,
  label,
  placeholder,
  description,
  onResetRequest,
  onSwitchToCreate
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ReturnType<typeof validatePasswordStrength> | null>(null);

  useEffect(() => {
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setValidation(null);
  }, [isOpen]);

  useEffect(() => {
    if (password && mode === "create") {
      setValidation(validatePasswordStrength(password));
    } else {
      setValidation(null);
    }
  }, [password, mode]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError("Password is required");
      return;
    }

    if (mode === "create") {
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (!validation?.isValid) {
        setError("Password does not meet security requirements");
        return;
      }
    }

    setIsLoading(true);
    try {
      await onSubmit(password);
    } catch (err: any) {
      setError(err.message || "Failed to process password");
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = (score: number) => {
    if (score <= 2) return "bg-red-500";
    if (score <= 4) return "bg-yellow-500";
    if (score <= 6) return "bg-blue-500";
    return "bg-green-500";
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div ref={modalRef} className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Dark Header */}
        <div className="flex items-center justify-between bg-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-700 rounded-lg">
              <span className="text-lg">{mode === "create" ? "🔐" : "🔓"}</span>
            </div>
            <h3 className="text-lg font-semibold text-white">
              {title || (mode === "create" ? "Create Master Password" : "Unlock Vault")}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-slate-700 transition-colors">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
          )}

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              {label || (mode === "create" ? "Create Master Password" : "Enter Master Password")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={placeholder || (mode === "create" ? "Create your master password" : "Enter your master password")}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {mode === "create" && validation && (
            <div className="mb-4">
              <div className="mb-2 flex gap-1">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full ${i < validation.score ? getStrengthColor(validation.score) : "bg-gray-200"}`} />
                ))}
              </div>
              {validation.feedback.length > 0 && (
                <ul className="text-xs text-gray-500 space-y-0.5">
                  {validation.feedback.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              )}
            </div>
          )}

          {mode === "create" && (
            <div className="mb-4">
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your master password"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {mode === "create" ? (
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800 mb-2 uppercase tracking-tight">Security Protocol Notice</h4>
                  <ul className="text-sm text-slate-600 space-y-2 leading-relaxed">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><strong>Permanent Encryption:</strong> No recovery option exists. We cannot reset or retrieve your password.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><strong>Data Persistence:</strong> All credentials will be permanently lost if this password is forgotten.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><strong>Offline Strategy:</strong> Store a physical copy in a secure, disconnected location.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 rounded-xl bg-indigo-50 border border-indigo-100 p-4 flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm text-indigo-600">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <p className="text-xs text-indigo-900 leading-relaxed font-medium">
                {description || (
                  <>
                    <strong>Zero-Knowledge Protocol:</strong> Encryption/decryption occurs exclusively in your browser. Your password is never transmitted.
                  </>
                )}
              </p>
            </div>
          )}

          {mode === "unlock" && onSwitchToCreate && (
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={onSwitchToCreate}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold underline transition-colors"
              >
                Want to start fresh with a new master password?
              </button>
            </div>
          )}
          {mode === "unlock" && onResetRequest && !onSwitchToCreate && (
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={onResetRequest}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold underline transition-colors"
              >
                Forgot your master password? Reset it here
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-teal-50">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="rounded-lg bg-green-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {isLoading ? "Processing..." : mode === "create" ? "Create Password" : "Unlock"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MasterPasswordModal;

